import './App.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReportPage from './ReportPage';
import InteriorPage from './InteriorPage';
import ExteriorPage from './ExteriorPage';
import CosmeticPage from './CosmeticPage';
import MechanicalPage from './MechanicalPage';
import StructuralPage from './StructuralPage';
import ElectricalOdorPage from './ElectricalOdorPage';
import AdditionalFeaturePage from './AdditionalFeaturePage';
import TirePage from './TirePage';
import DamageReportPage from './DamageReportPage';
import UnderstandingPage from './UnderstandingPage';


import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
const queryClient = new QueryClient();

function App() {
  const [inspectionId, setInspectionId] = useState(null);
  const [inspectionCode, setInspectionCode] = useState(null);
  const [inspectionData, setInspectionData] = useState(null);

  const [interiorDamageChecklist, setInteriorDamageChecklist] = useState([]);
  const [interiorImages, setInteriorImages] = useState({});

  const [exteriorDamageChecklist, setExteriorDamageChecklist] = useState([]);
  const [exteriorImages, setExteriorImages] = useState({});

  const [cosmeticDamageChecklist, setCosmeticDamageChecklist] = useState([]);
  const [cosmeticImages, setCosmeticImages] = useState({});

  const [mechanicalDamageChecklist, setMechanicalDamageChecklist] = useState([]);
  const [mechanicalImages, setMechanicalImages] = useState({});

  const [structuralDamageEngineChecklist, setStructuralDamageEngineChecklist] = useState([]);
  const [structuralDamageBodyChecklist, setStructuralDamageBodyChecklist] = useState([]);
  const [structuralImages, setStructuralImages] = useState({});

  const [electricalDamageChecklist, setElectricalDamageChecklist] = useState([]);
  const [odorDamageChecklist, setOdorDamageChecklist] = useState([]);
  const [obdData, setObdData] = useState([]);

  const [damageReportData, setDamageReportData] = useState([]);
  const [tireData, setTireData] = useState([]);
  const [additionalFeatures, setAdditionalFeatures] = useState([]);

  const [error, setError] = useState(null);

  const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  /* ------------------------------------------------------------------ */
  /*  Fetch all report data                                             */
  /* ------------------------------------------------------------------ */
  async function fetchInspection() {
    try {
      setError(null);

      /* reset all children state for a clean load */
      setInspectionData(null);
      setInteriorDamageChecklist([]); setInteriorImages({});
      setExteriorDamageChecklist([]); setExteriorImages({});
      setCosmeticDamageChecklist([]); setCosmeticImages({});
      setMechanicalDamageChecklist([]); setMechanicalImages({});
      setStructuralDamageEngineChecklist([]); setStructuralDamageBodyChecklist([]);
      setStructuralImages({});
      setElectricalDamageChecklist([]); setOdorDamageChecklist([]);
      setObdData([]);
      setDamageReportData([]); setTireData([]); setAdditionalFeatures([]);

      /* 1) Main inspection header ------------------------------------------------ */
      const res1 = await axios.get(
        `${apiURL}/inspection?inspectionId=${inspectionId}&inspectionCode=${inspectionCode}`
      );
      setInspectionData(res1.data);

      /* 2) Interior --------------------------------------------------------------- */
      const res2 = await axios.get(`${apiURL}/inspection/${inspectionId}/interiordamagechecklist`);
      const res3 = await axios.get(`${apiURL}/inspection/${inspectionId}/interiorbase`);
      setInteriorDamageChecklist(res2.data);
      setInteriorImages(res3.data || {});

      /* 3) Exterior --------------------------------------------------------------- */
      const res4 = await axios.get(`${apiURL}/inspection/${inspectionId}/exteriordamagechecklist`);
      const res5 = await axios.get(`${apiURL}/inspection/${inspectionId}/exteriorbase`);
      setExteriorDamageChecklist(res4.data);
      setExteriorImages(res5.data || {});

      /* 4) Cosmetic --------------------------------------------------------------- */
      const res6 = await axios.get(`${apiURL}/inspection/${inspectionId}/cosmeticdamagechecklist`);
      const res7 = await axios.get(`${apiURL}/inspection/${inspectionId}/cosmeticbase`);
      setCosmeticDamageChecklist(res6.data);
      setCosmeticImages(res7.data || {});

      /* 5) Mechanical ------------------------------------------------------------- */
      const res8 = await axios.get(`${apiURL}/inspection/${inspectionId}/mechanicaldamagechecklist`);
      const res9 = await axios.get(`${apiURL}/inspection/${inspectionId}/mechanicalbase`);
      setMechanicalDamageChecklist(res8.data);
      setMechanicalImages(res9.data || {});

      /* 6) Structural ------------------------------------------------------------- */
      const res10A = await axios.get(`${apiURL}/inspection/${inspectionId}/structuraldamageenginechecklist`);
      const res10B = await axios.get(`${apiURL}/inspection/${inspectionId}/structuraldamagebodychecklist`);
      const res11 = await axios.get(`${apiURL}/inspection/${inspectionId}/structuralbase`);
      setStructuralDamageEngineChecklist(res10A.data || []);
      setStructuralDamageBodyChecklist(res10B.data || []);
      setStructuralImages(res11.data || {});

      /* 7) Electrical & Odor ------------------------------------------------------ */
      const res12 = await axios.get(`${apiURL}/inspection/${inspectionId}/electricaldamagechecklist`);
      const res13 = await axios.get(`${apiURL}/inspection/${inspectionId}/odordamagechecklist`);
      setElectricalDamageChecklist(res12.data || []);
      setOdorDamageChecklist(res13.data || []);

      /* 8) OBD ------------------------------------------------------------------- */
      const res14 = await axios.get(`${apiURL}/inspection/${inspectionId}/inspectionobd`);
      setObdData(res14.data || []);

      /* 9) Damage report ---------------------------------------------------------- */
      const res15 = await axios.get(`${apiURL}/inspection/${inspectionId}/damagereport`);
      setDamageReportData(res15.data || []);

      /* 10) Tires & brakes -------------------------------------------------------- */
      const res16 = await axios.get(`${apiURL}/inspection/${inspectionId}/inspectiontire`);
      setTireData(res16.data || []);

      /* 11) Additional features --------------------------------------------------- */
      const res17 = await axios.get(`${apiURL}/inspection/${inspectionId}/inspectionadditionalfeature`);
      setAdditionalFeatures(res17.data || []);

    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Error fetching inspection');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Extract ID & code from URL once on mount                          */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get('inspectionId');
    const codeParam = params.get('inspectionCode');
    if (idParam && codeParam) {
      setInspectionId(idParam);
      setInspectionCode(codeParam);
    } else {
      setError('Missing inspectionId or inspectionCode in URL');
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Fetch data when both id & code are present                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (inspectionId && inspectionCode) fetchInspection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId, inspectionCode]);

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <QueryClientProvider client={queryClient}>
      <div style={{ margin: 0, padding: 10, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {error && <p style={{ color: 'red' }}>{error}</p>}

        {inspectionData && <ReportPage inspection={inspectionData} />}

        <UnderstandingPage />

        {(interiorDamageChecklist.length || Object.keys(interiorImages).length) > 0 && (
          <InteriorPage checklist={interiorDamageChecklist} images={interiorImages} />
        )}

        {(exteriorDamageChecklist.length || Object.keys(exteriorImages).length) > 0 && (
          <ExteriorPage checklist={exteriorDamageChecklist} images={exteriorImages} />
        )}

        {(cosmeticDamageChecklist.length || Object.keys(cosmeticImages).length) > 0 && (
          <CosmeticPage
            checklist={cosmeticDamageChecklist}
            images={cosmeticImages}
            paintThicknessHigh={inspectionData?.paintThicknessHigh}
            paintThicknessLow={inspectionData?.paintThicknessLow}
          />
        )}

        {(mechanicalDamageChecklist.length || Object.keys(mechanicalImages).length) > 0 && (
          <MechanicalPage checklist={mechanicalDamageChecklist} images={mechanicalImages} />
        )}

        {(structuralDamageEngineChecklist.length ||
          structuralDamageBodyChecklist.length ||
          Object.keys(structuralImages).length) > 0 && (
          <StructuralPage
            engineChecklist={structuralDamageEngineChecklist}
            bodyChecklist={structuralDamageBodyChecklist}
            images={structuralImages}
          />
        )}

        {(electricalDamageChecklist.length || odorDamageChecklist.length || obdData.length) > 0 && (
          <ElectricalOdorPage
            electricalChecklist={electricalDamageChecklist}
            odorChecklist={odorDamageChecklist}
            obdcData={obdData} 
          />
        )}

        {additionalFeatures.length > 0 && <AdditionalFeaturePage features={additionalFeatures} />}

        {tireData.length > 0 && <TirePage tireData={tireData} />}

        {damageReportData.length > 0 && <DamageReportPage damageData={damageReportData} />}
      </div>
    </QueryClientProvider>
  );
}

export default App;
